import { LoginRequestBody, RegistrationMode } from '@easeltv/client-sdk';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useApi } from '../../../hooks/useApi/useApi';
import { useConfig } from '../../../hooks/useConfig/useConfig';
import { useDictionaryObject } from '../../../hooks/useDictionary/useDictionary';
import { useAnalytics } from '../../../providers/AnalyticsProvider/AnalyticsProvider';
import { useAuth } from '../../../providers/AuthProvider/AuthProvider';
import { Field } from '../../../providers/RegistrationProvider/RegistrationProvider.helpers';
import { SetErrorFn } from '../../Blocks/FormBlock';
import { handleRegistrationError } from '../../Sections/RegistrationSection/RegistrationSection.helpers';
import DumbSignInTemplate from './SignInTemplate.dumb';

/* -------------------------------- Component ------------------------------- */

/**
 * SignInTemplate
 *
 * @returns JSX.Element
 */
const SignInTemplate = ({
    disableRegistration = false,
}: {
    disableRegistration?: boolean;
}) => {
    // Get values from the dictionary

    const labels = useDictionaryObject({
        headingBody: 'login.header.body',
        headingSubtitle: 'login.header.subtitle',
        headingTitle: 'login.header.title',
        registerButton: 'login.registration.button',
        registerTitle: 'login.registration.title',
        submitLabel: 'login.button.submit',
        disclaimerText: 'login.terms.text',
        disclaimerLabel: 'login.terms.link.label',
        disclaimerUrl: 'login.terms.link.url',
        emailPlaceholder: 'login.placeholder.email',
        passwordPlaceholder: 'login.placeholder.password',
    });

    // Get the mode of registration
    const {
        registration: { mode },
    } = useConfig();

    const api = useApi();
    const { login } = useAuth();

    // Retrieve pairing code from history state if it exists
    const location = useLocation();
    const {
        code,
        returnUrl = '/',
        ...state
    } = (location.state as { code?: string; returnUrl?: string }) ?? {};

    const { updatePageObject } = useAnalytics();

    // Can be used to programmatically navigate page
    // https://reactrouter.com/docs/en/v6/api#usenavigate
    const navigate = useNavigate();

    // Callback to be run when the sign in form is submitted
    const handleOnSubmit = async (
        data: LoginRequestBody,
        setError: SetErrorFn,
    ): Promise<void> => {
        // If the client validation passes, send data to server
        const loginResult = await login(data);

        // Decipher error message to show based on sdk error
        if (!loginResult.ok) {
            return handleRegistrationError(loginResult.payload).forEach(
                ([key, value]) => {
                    setError(key)(value[0]);
                },
            );
        }

        // track successful sign in before redirect
        window.analytics.trigger({
            type: 'account.signed-in',
            userId: loginResult.payload.id,
        });

        // Successful login attempt with no pairing attempt should redirect to the homepage
        if (!code) {
            return navigate(returnUrl, { replace: true, state });
        }

        // If a pairing code exists we should attempt to pair after signin
        const pairingResult = await api.pairDevice(code.toLowerCase());

        if (!pairingResult.ok) {
            // @TODO: Error Handling
            // eslint-disable-next-line no-console
            console.trace(pairingResult);

            // Failed pairing will redirect to home page for now
            return navigate('/pair');
        }

        const pairingResponse = pairingResult.payload;

        if (pairingResponse.body.wantsToPurchase) {
            // IDs are delivered namespaced by the item type - for now that is limited to Products but in the future
            // could include Collections

            // track successful sign in before redirect
            window.analytics.trigger({
                type: 'pairing.completed',
                wantsToPurchase: pairingResponse.body.wantsToPurchase,
            });

            const url = pairingResponse.body.wantsToPurchase
                .split('_')
                .join('/');

            return navigate(`/${url}/pay`);
        }

        // track successful sign in before redirect
        window.analytics.trigger({
            type: 'pairing.completed',
        });

        // Redirect to pairing success page
        return navigate('/pair', { state: { success: true } });
    };

    // We use the same form block as the registration page so need to feed in the appropriate field config
    const fields: Field[] = [
        {
            label: 'login.label.email',
            placeholder: labels.emailPlaceholder,
            id: 'email',
            type: 'email',
            required: true,
        },
        {
            label: 'login.label.password',
            placeholder: labels.passwordPlaceholder,
            id: 'password',
            type: 'password',
            required: true,
        },
    ];

    // If invite only there should be no way to register
    const showOtherRegOption =
        !disableRegistration && mode !== RegistrationMode.InviteOnly;

    useEffect(() => {
        updatePageObject({
            pageId: 'login',
            pageName: 'Login',
            pageType: 'system',
        });
    }, [updatePageObject]);

    useEffect(() => {
        window.scrollTo(0, 0);
    });

    return (
        <DumbSignInTemplate
            fields={fields}
            handleOnSubmit={handleOnSubmit}
            labels={labels}
            showOtherRegOption={showOtherRegOption}
            disableRegistration={disableRegistration}
        />
    );
};

/* --------------------------------- Export --------------------------------- */

export default SignInTemplate;
